/** Display helpers. Pure; `now` is injectable for tests. */

export function duration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0s'
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`
  const h = Math.floor(m / 60)
  return `${h}h ${String(m % 60).padStart(2, '0')}m`
}

/** How long a run or step took, or has taken so far. `stoppedAt` ends a run that stopped without finishing (failed). */
export function elapsed(startedAt: string | null, finishedAt: string | null, now = Date.now(), stoppedAt?: string | null): string | null {
  if (!startedAt) return null
  const end = finishedAt ? Date.parse(finishedAt) : stoppedAt ? Date.parse(stoppedAt) : now
  return duration(end - Date.parse(startedAt))
}

const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })
const dateFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

export function when(isoString: string | null, now = Date.now()): string {
  if (!isoString) return ''
  const t = Date.parse(isoString)
  const d = new Date(t)
  const sameDay = new Date(now).toDateString() === d.toDateString()
  return sameDay ? timeFmt.format(d) : `${dateFmt.format(d)}, ${timeFmt.format(d)}`
}

export function ago(isoString: string | null, now = Date.now()): string {
  if (!isoString) return 'never'
  const s = Math.max(0, Math.floor((now - Date.parse(isoString)) / 1000))
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

export const TEMPLATE_LABEL: Record<string, string> = {
  full: 'Full',
  adopt: 'Adopt',
  boxOnly: 'Box only',
  addPerson: 'Add person',
  teardown: 'Teardown',
}

export function money(n: number): string {
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}/mo`
}

/** Short display for a step id value: strings and numbers as-is, objects as compact JSON. */
export function idValue(v: unknown): string {
  if (v === null || v === undefined) return '–'
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (Array.isArray(v) && v.every((x) => typeof x === 'string' || typeof x === 'number')) return v.join(', ')
  return JSON.stringify(v)
}
